import {
	type RunDetailStage,
	type RunDetailTone,
	RunDetailWorkspace,
} from "@/components/run-detail/run-detail-workspace";
import { Button } from "@/components/ui/button";
/**
 * M01 TraceDetail · 调试端右栏 · 单条 trace 展开
 *
 * 承接：T11 §4.2 DebugView 右侧 ResizablePanel · 数据源 admin/v1/trace/{trace_id}
 *
 * Day 9 增强：
 * - 提取 events[].phase=intent_recognition / playbook_recommendation 渲染意图/方案区块
 * - 5 主干 span timeline / Guardrail 红绿灯仍为 S2+ 占位（PR-B 简版 JSON 兜底）
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
	type IntentRecognitionEvent,
	type PlaybookRecommendationEvent,
	type TraceDetail as TraceDetailResponse,
	type TraceEvalSummary,
	type TraceRuntimeCapabilities,
	pickIntentEvent,
	pickPlaybookEvent,
	useTraceDetail,
} from "@/lib/api/trace";
import { intentLabel, playbookLabel, sampleLabel, skillLabel } from "@/lib/product-labels";
import { ArrowRight } from "lucide-react";
import { useMemo, useState } from "react";

interface Props {
	traceId: string | null;
}

type TraceStageId = "input_context" | "understanding_plan" | "evidence" | "reply_quality" | "audit";

export function TraceDetail({ traceId }: Props) {
	const [activeStageId, setActiveStageId] = useState<TraceStageId>("input_context");
	const [completeDisclosure, setCompleteDisclosure] = useState(false);
	const { data, isLoading, isError, error } = useTraceDetail(traceId);
	const stages = useMemo(() => (data ? buildTraceRunStages(data) : []), [data]);

	if (traceId === null) {
		return (
			<Card data-testid="trace-empty">
				<CardHeader>
					<CardTitle>选择会话查看 Trace</CardTitle>
					<CardDescription>
						左侧选择一个会话 → 右侧渲染对应 trace 详情（5 主干 span + 4 Phase span +
						version_snapshot · S5 T8 接通后真渲染）
					</CardDescription>
				</CardHeader>
			</Card>
		);
	}

	if (isLoading) {
		return (
			<div data-testid="trace-loading" className="p-4 text-sm text-text-secondary">
				加载 Trace 中...
			</div>
		);
	}

	if (isError) {
		return (
			<div data-testid="trace-error" className="p-4 text-sm text-brand-danger">
				加载失败：{error instanceof Error ? error.message : String(error)}
			</div>
		);
	}

	if (!data) {
		return null;
	}

	const intentEvent = pickIntentEvent(data.events);
	const playbookEvent = pickPlaybookEvent(data.events);
	const statusTone = finalOutcomeTone(data.final_outcome);
	const replayMessages = traceReplayMessages(data);
	const replayHref = replayMessages.length > 0 ? buildTraceReplayHref(data, replayMessages) : null;

	return (
		<div className="flex flex-col gap-3" data-testid="trace-detail">
			<Card data-testid="trace-replay-controls">
				<CardHeader>
					<CardTitle>历史运行回放</CardTitle>
					<CardDescription>
						已发生过的会话记录。这里和链路测试台复用同一套运行详情，用于看清每一步为什么这样答。
					</CardDescription>
				</CardHeader>
				<CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
					<div className="space-y-1">
						<span className="inline-flex items-center gap-2 text-sm text-text-secondary">
							<Switch
								aria-label="回放完整披露"
								checked={completeDisclosure}
								onCheckedChange={setCompleteDisclosure}
							/>
							完整披露
						</span>
						<p className="text-xs text-text-muted">
							{replayMessages.length > 0
								? `可带 ${replayMessages.length} 条本轮问答和 session 继续复跑。`
								: "本条 trace 未返回本轮问答，暂不能带上下文复跑。"}
						</p>
					</div>
					{replayHref ? (
						<Button asChild variant="outline" size="sm">
							<a href={replayHref}>
								<ArrowRight className="mr-2 h-4 w-4" aria-hidden="true" />
								用此回放继续测试
							</a>
						</Button>
					) : null}
				</CardContent>
			</Card>

			<TraceRuntimeSnapshotCard data={data} />

			{data.eval_summary ? <TraceEvalSummaryCard summary={data.eval_summary} /> : null}

			<RunDetailWorkspace
				headline={`Trace ${data.trace_id}`}
				description={`Session ${data.session_id} · Turn ${data.turn_index} · ${data.total_latency_ms}ms · ${data.final_outcome}`}
				statusLabel={finalOutcomeLabel(data.final_outcome)}
				statusTone={statusTone}
				badges={[
					{ label: `动作：${data.final_action_type}`, tone: statusTone },
					{ label: `格式：${data.reply_format}`, tone: "neutral" },
				]}
				stages={stages}
				activeStageId={activeStageId}
				onSelectStage={setActiveStageId}
				completeDisclosure={completeDisclosure}
				summaryTestId="trace-run-summary"
				flowTestId="trace-run-flow"
				inspectorTestId="trace-stage-inspector"
			/>

			{intentEvent && <IntentSection event={intentEvent} />}
			{playbookEvent && <PlaybookSection event={playbookEvent} />}
		</div>
	);
}

function TraceRuntimeSnapshotCard({ data }: { data: TraceDetailResponse }) {
	const capabilities = data.version_snapshot.runtime_capabilities;
	return (
		<Card data-testid="trace-runtime-snapshot">
			<CardHeader>
				<CardTitle>历史运行能力口径</CardTitle>
				<CardDescription>
					来自
					Trace.version_snapshot.runtime_capabilities，用来判断这条历史记录当时用了哪些真实能力。
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-3">
				<dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm lg:grid-cols-4">
					<Field
						label="数据口径"
						value={dataModeLabel(data.version_snapshot.data_mode)}
						testid="trace-runtime-data-mode"
					/>
					<Field
						label="主回复"
						value={runtimeCapabilityLabel(capabilities, "phase4_runner_mode", phase4RunnerLabel)}
						testid="trace-runtime-runner"
					/>
					<Field
						label="Prompt"
						value={runtimeCapabilityLabel(capabilities, "prompt_effect_mode", promptEffectLabel)}
						testid="trace-runtime-prompt"
					/>
					<Field
						label="知识召回"
						value={runtimeCapabilityLabel(capabilities, "rag_mode", ragModeLabel)}
						testid="trace-runtime-rag"
					/>
					<Field
						label="会话"
						value={runtimeCapabilityLabel(capabilities, "session_mode", sessionModeLabel)}
						testid="trace-runtime-session"
					/>
					<Field
						label="质检"
						value={runtimeCapabilityLabel(capabilities, "quality_mode", qualityModeLabel)}
						testid="trace-runtime-quality"
					/>
					<Field
						label="人工"
						value={runtimeCapabilityLabel(capabilities, "handoff_mode", handoffModeLabel)}
						testid="trace-runtime-handoff"
					/>
					<Field
						label="Shopify 读"
						value={runtimeCapabilityLabel(capabilities, "shopify_read_mode", shopifyReadLabel)}
						testid="trace-runtime-shopify-read"
					/>
					<Field
						label="Shopify 写"
						value={runtimeCapabilityLabel(capabilities, "shopify_write_mode", shopifyWriteLabel)}
						testid="trace-runtime-shopify-write"
					/>
				</dl>
				<p className="text-xs leading-5 text-text-muted">
					字段来源：Trace.version_snapshot.runtime_capabilities。若历史 Trace
					未记录某项，页面展示“未记录”，不把未知能力包装成已完成。
				</p>
			</CardContent>
		</Card>
	);
}

function TraceEvalSummaryCard({ summary }: { summary: TraceEvalSummary }) {
	const tone = closeRecommendationTone(summary.close_recommendation);
	return (
		<Card data-testid="trace-eval-summary">
			<CardHeader>
				<div className="flex flex-wrap items-center justify-between gap-2">
					<div>
						<CardTitle>评测摘要</CardTitle>
						<CardDescription>
							这条 Trace 已关联到 M07 Badcase，可用于判断修复是否可以收口。
						</CardDescription>
					</div>
					<span className={`rounded border px-2 py-1 text-xs font-medium ${tone.className}`}>
						{tone.label}
					</span>
				</div>
			</CardHeader>
			<CardContent>
				<dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm lg:grid-cols-4">
					<Field
						label="样本"
						value={summary.sample_id ? sampleLabel(summary.sample_id) : "未关联"}
						testid="trace-eval-sample"
					/>
					<Field
						label="Badcase"
						value={summary.badcase_id ?? "未关联"}
						testid="trace-eval-badcase"
					/>
					<Field
						label="复跑结果"
						value={rerunStatusLabel(summary.rerun_status)}
						testid="trace-eval-rerun"
					/>
					<Field label="关闭建议" value={summary.close_reason} testid="trace-eval-close-reason" />
					<Field
						label="期望意图"
						value={intentLabel(summary.expected_intent)}
						testid="trace-eval-expected-intent"
					/>
					<Field
						label="实际意图"
						value={intentLabel(summary.actual_intent)}
						testid="trace-eval-actual-intent"
					/>
					<Field
						label="期望方案"
						value={playbookLabel(summary.expected_pb)}
						testid="trace-eval-expected-pb"
					/>
					<Field
						label="实际方案"
						value={playbookLabel(summary.actual_pb)}
						testid="trace-eval-actual-pb"
					/>
				</dl>
			</CardContent>
		</Card>
	);
}

function dataModeLabel(value: string | null): string {
	if (value === "mock") return "mock（模拟）";
	if (value === "sandbox") return "sandbox（沙盒）";
	if (value === "real_read") return "real_read（真实只读）";
	if (value === "real_write") return "real_write（真实写入）";
	return "未记录";
}

function runtimeCapabilityLabel(
	capabilities: TraceRuntimeCapabilities | null,
	key: keyof TraceRuntimeCapabilities,
	formatter: (value: string) => string,
): string {
	const value = capabilities?.[key];
	return value ? formatter(value) : "未记录";
}

function phase4RunnerLabel(value: string): string {
	if (value === "sdk_loop") return "SDK 工具循环";
	if (value === "sdk_loop_fallback") return "SDK 回退";
	if (value === "llm") return "主 Agent LLM";
	if (value === "llm_fallback") return "LLM 回退";
	if (value === "custom") return "自定义实现";
	if (value === "static") return "静态模板";
	return value;
}

function promptEffectLabel(value: string): string {
	if (value === "consumed_by_llm") return "LLM 已消费";
	if (value === "preview_only") return "仅预览";
	if (value === "rendered_not_consumed") return "已渲染未消费";
	if (value === "unknown") return "未知";
	return value;
}

function ragModeLabel(value: string): string {
	if (value === "qdrant_live") return "Qdrant 真实";
	if (value === "configured") return "已配置";
	if (value === "error") return "召回异常";
	if (value === "null") return "未接入";
	return value;
}

function sessionModeLabel(value: string): string {
	if (value === "pg_session") return "PG 持久会话";
	if (value === "redis_pg_session") return "Redis+PG 会话";
	if (value === "request_only") return "请求内会话";
	return value;
}

function qualityModeLabel(value: string): string {
	if (value === "q_deep_side_effect") return "Q-deep 已启用";
	if (value === "q_fast_grounding_side_effect") return "Q-fast + 副作用门";
	if (value === "q_fast_grounding_starter") return "Q-fast starter";
	if (value === "custom") return "自定义质检";
	return value;
}

function handoffModeLabel(value: string): string {
	if (value === "ticket_created") return "工单已创建";
	if (value === "agent_connected") return "人工已接入";
	if (value === "contract_only") return "合同预留";
	if (value === "placeholder") return "人工占位";
	if (value === "disabled") return "禁用";
	return value;
}

function shopifyReadLabel(value: string): string {
	if (value === "live_read") return "真实读取";
	if (value === "failed_or_blocked") return "读取受限";
	if (value === "not_requested") return "未请求";
	return value;
}

function shopifyWriteLabel(value: string): string {
	if (value === "dry_run") return "Dry-run";
	if (value === "live_confirmed") return "真实写入确认";
	if (value === "blocked") return "阻断";
	if (value === "disabled") return "禁用";
	return value;
}

function traceRouteLabel(event: PlaybookRecommendationEvent | null): string {
	if (!event) return "未返回";
	if (event.short_circuit_skill) return skillLabel(event.short_circuit_skill);
	return playbookLabel(event.playbook_id ?? "无方案命中");
}

function buildTraceRunStages(data: TraceDetailResponse): Array<RunDetailStage<TraceStageId>> {
	const intentEvent = pickIntentEvent(data.events);
	const playbookEvent = pickPlaybookEvent(data.events);
	const phase4Event = pickTracePhaseEvent(data, "phase4_generate");
	const phase5Event = pickTracePhaseEvent(data, "phase5_validate");
	const eventNames = data.events.map((event) =>
		String((event as { phase?: unknown }).phase ?? "unknown"),
	);
	const phaseSummary = eventNames.join(" / ") || "未返回";
	const routeLabel = traceRouteLabel(playbookEvent);

	return [
		{
			id: "input_context",
			title: "用户输入与上下文",
			status: "通过",
			tone: "success",
			summary: `回放记录来自 session=${shortId(data.session_id)}，当前为第 ${data.turn_index + 1} 轮。`,
			primaryFields: [
				field(
					"本轮问题",
					traceEventText(data, "user_message"),
					"conversation_io.user_message",
					"Trace",
				),
				field("商户", data.merchant_id, "merchant_id", "Trace"),
				field("客户", data.customer_id ?? "未返回", "customer_id", "Trace"),
				field("会话", data.session_id, "session_id", "Trace"),
				field("轮次", String(data.turn_index + 1), "turn_index", "Trace"),
			],
			raw: { trace_id: data.trace_id, session_id: data.session_id, customer_id: data.customer_id },
		},
		{
			id: "understanding_plan",
			title: "理解与计划",
			status: intentEvent ? "通过" : "受限",
			tone: intentEvent ? "success" : "warning",
			summary: intentEvent
				? `意图分类为 ${intentEvent.intent}，处理路径 ${routeLabel}。`
				: "trace 未返回意图事件，需要查看原始事件确认是否缺契约。",
			primaryFields: [
				field(
					"意图分类",
					intentEvent?.intent ?? "未返回",
					"intent_recognition.intent",
					"Trace event",
				),
				field(
					"分类把握",
					intentEvent ? intentEvent.confidence.toFixed(2) : "未返回",
					"intent_recognition.confidence",
					"Trace event",
				),
				field(
					"当前对话阶段",
					intentEvent?.service_stage ?? "未返回",
					"intent_recognition.service_stage",
					"Trace event",
				),
				field("处理路径", routeLabel, "playbook_recommendation.playbook_id", "Trace event"),
				field(
					"匹配方式",
					playbookEvent?.match_mode ?? "未返回",
					"playbook_recommendation.match_mode",
					"Trace event",
				),
				field(
					"推荐工具",
					playbookEvent?.recommended_skills.join("、") || "未返回",
					"playbook_recommendation.recommended_skills",
					"Trace event",
				),
			],
			raw: { intentEvent, playbookEvent },
		},
		{
			id: "evidence",
			title: "事实与证据",
			status: data.degraded_modules.length > 0 ? "受限" : "跳过",
			tone: data.degraded_modules.length > 0 ? "warning" : "neutral",
			summary:
				data.degraded_modules.length > 0
					? `存在降级模块：${data.degraded_modules.join("、")}。`
					: "当前 trace 未返回工具或知识证据详情；如结果异常，应回到链路测试台复跑查看 MessagesResponse。",
			primaryFields: [
				field("降级模块", data.degraded_modules.join("、") || "无", "degraded_modules", "Trace"),
				field("事件数量", String(data.events.length), "events", "Trace"),
				field("证据事件", phaseSummary, "events[].phase", "Trace"),
			],
			raw: { degraded_modules: data.degraded_modules, events: data.events },
		},
		{
			id: "reply_quality",
			title: "回复与质检",
			status: data.final_outcome === "failed" ? "受限" : "通过",
			tone: finalOutcomeTone(data.final_outcome),
			summary: `最终动作 ${data.final_action_type}，处理结果 ${finalOutcomeLabel(data.final_outcome)}；${traceGroundingCheckLabel(phase5Event)}。`,
			primaryFields: [
				field("最终动作", data.final_action_type, "final_action_type", "Trace"),
				field("处理结果", data.final_outcome, "final_outcome", "Trace"),
				field(
					"主 Agent 回退",
					traceRunnerErrorLabel(phase4Event),
					"events.phase4_generate.summary.runner_error",
					"Trace event",
				),
				field(
					"Grounding 检查",
					traceGroundingCheckLabel(phase5Event),
					"events.phase5_validate.summary.grounding_check",
					"Trace event",
				),
				field(
					"Side-effect 检查",
					traceSideEffectCheckLabel(phase5Event),
					"events.phase5_validate.summary.side_effect_check",
					"Trace event",
				),
				field(
					"Step 使用",
					traceStepUsageLabel(phase5Event),
					"events.phase5_validate.summary.step_used",
					"Trace event",
				),
				field(
					"确认计划",
					traceConfirmationPlanLabel(phase4Event),
					"events.phase4_generate.summary.confirmation_plan",
					"Trace event",
				),
				field("转人工原因", data.handoff_reason ?? "无", "handoff_reason", "Trace"),
				field("回复格式", data.reply_format, "reply_format", "Trace"),
				field("估算 token", String(data.estimated_token_count), "estimated_token_count", "Trace"),
			],
			raw: {
				final_action_type: data.final_action_type,
				final_outcome: data.final_outcome,
				handoff_reason: data.handoff_reason,
				phase4Event,
				phase5Event,
			},
		},
		{
			id: "audit",
			title: "审计与沉淀",
			status: "通过",
			tone: "success",
			summary: `事件列表：${phaseSummary}。可复制 trace/session 用于研发定位。`,
			primaryFields: [
				field("运行记录", data.trace_id, "trace_id", "Trace"),
				field("会话", data.session_id, "session_id", "Trace"),
				field("总耗时", `${data.total_latency_ms}ms`, "total_latency_ms", "Trace"),
				field("事件列表", phaseSummary, "events[].phase", "Trace"),
				field(
					"API 版本",
					data.version_snapshot.api_version,
					"version_snapshot.api_version",
					"Trace",
				),
			],
			raw: data,
		},
	];
}

function field(label: string, value: string, rawName: string, source: string) {
	return { label, value: value || "未返回", rawName, source };
}

function traceEventText(data: TraceDetailResponse, key: string): string {
	return traceEventString(data, key) ?? "未返回";
}

function traceEventString(data: TraceDetailResponse, key: string): string | null {
	const conversationEvent = data.events.find(
		(event) => (event as { phase?: unknown }).phase === "conversation_io",
	);
	if (!conversationEvent || typeof conversationEvent !== "object") return null;
	const value = (conversationEvent as Record<string, unknown>)[key];
	return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function pickTracePhaseEvent(
	data: TraceDetailResponse,
	phase: string,
): Record<string, unknown> | null {
	const event = data.events.find((item) => (item as { phase?: unknown }).phase === phase);
	return asRecord(event);
}

function traceConfirmationPlanLabel(phase4Event: Record<string, unknown> | null): string {
	const summary = asRecord(phase4Event?.summary);
	const plan = asRecord(summary?.confirmation_plan);
	if (!plan) return "无确认计划";
	const status = String(summary?.confirmation_status ?? "pending_user_confirmation");
	const skillId = String(plan.skill_id ?? "未知 Skill");
	const toolId = String(plan.tool_id ?? "未知工具");
	const auditId = String(plan.audit_id ?? summary?.write_audit_id ?? "");
	const auditStatus = String(summary?.write_audit_status ?? "");
	const mutationLabel = plan.mutation_executed === false ? "不执行真实写入" : "写入状态待核验";
	const auditLabel = auditId ? `；审计 ${auditId}${auditStatus ? `(${auditStatus})` : ""}` : "";
	return `${confirmationStatusLabel(status)}；${skillId} / ${toolId}；${mutationLabel}${auditLabel}`;
}

function traceRunnerErrorLabel(phase4Event: Record<string, unknown> | null): string {
	const summary = asRecord(phase4Event?.summary);
	const value = summary?.runner_error;
	return typeof value === "string" && value.trim().length > 0 ? value.trim() : "无";
}

function traceGroundingCheckLabel(phase5Event: Record<string, unknown> | null): string {
	const check = asRecord(asRecord(phase5Event?.summary)?.grounding_check);
	if (!check) return "Grounding 未返回";
	if (check.triggered !== true) return "Grounding 未触发";
	const ratio = traceNumber(check.grounded_ratio);
	const ratioLabel = ratio === null ? "覆盖未返回" : `覆盖 ${Math.round(ratio * 100)}%`;
	if (check.passed === false) {
		const unsupported = traceStringList(check.unsupported_claims);
		return `未通过 · ${unsupported.length > 0 ? unsupported.join("、") : "存在未支撑表述"}`;
	}
	if (check.passed === true) return `通过 · ${ratioLabel}`;
	return `未判定 · ${ratioLabel}`;
}

function traceSideEffectCheckLabel(phase5Event: Record<string, unknown> | null): string {
	const check = asRecord(asRecord(phase5Event?.summary)?.side_effect_check);
	if (!check) return "未返回";
	if (check.triggered !== true) return "未触发";
	const issues = traceStringList(check.issues);
	if (check.passed === false) {
		return `未通过 · ${issues.length > 0 ? issues.join("、") : "存在副作用风险"}`;
	}
	if (check.passed === true) return "通过 · 无副作用风险";
	return "未判定";
}

function traceStepUsageLabel(phase5Event: Record<string, unknown> | null): string {
	const summary = asRecord(phase5Event?.summary);
	const used = traceNumber(summary?.step_used);
	const budget = traceNumber(summary?.step_budget);
	const terminatedBy =
		typeof summary?.terminated_by === "string" && summary.terminated_by.trim()
			? summary.terminated_by.trim()
			: "未返回";
	if (used === null && budget === null) return "未返回";
	return `${used ?? "未返回"} / ${budget ?? "未返回"}；终止=${traceTerminatedByLabel(terminatedBy)}`;
}

function traceNumber(value: unknown): number | null {
	if (typeof value === "number" && Number.isFinite(value)) return value;
	if (typeof value === "string" && value.trim() !== "") {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : null;
	}
	return null;
}

function traceStringList(value: unknown): string[] {
	if (!Array.isArray(value)) return [];
	return value.map((item) => String(item)).filter((item) => item.length > 0);
}

function traceTerminatedByLabel(value: string): string {
	if (value === "natural") return "自然结束";
	if (value === "step_budget") return "Step 预算耗尽";
	if (value === "safety_block") return "安全阻断";
	if (value === "runner_error") return "运行器异常";
	return value || "未返回";
}

function confirmationStatusLabel(status: string): string {
	if (status === "pending_user_confirmation") return "等待用户确认";
	if (status === "confirmed") return "已确认";
	if (status === "rejected") return "已拒绝";
	if (status === "expired") return "已过期";
	if (status === "not_required") return "无需确认";
	return status || "等待用户确认";
}

function asRecord(value: unknown): Record<string, unknown> | null {
	return typeof value === "object" && value !== null && !Array.isArray(value)
		? (value as Record<string, unknown>)
		: null;
}

function traceReplayMessages(
	data: TraceDetailResponse,
): Array<{ role: "user" | "assistant"; content: string }> {
	const userMessage = traceEventString(data, "user_message");
	const assistantReply = traceEventString(data, "assistant_reply");
	const messages: Array<{ role: "user" | "assistant"; content: string }> = [];
	if (userMessage) {
		messages.push({ role: "user", content: userMessage });
	}
	if (assistantReply) {
		messages.push({ role: "assistant", content: assistantReply });
	}
	return messages;
}

function buildTraceReplayHref(
	data: TraceDetailResponse,
	messages: Array<{ role: "user" | "assistant"; content: string }>,
): string {
	const params = new URLSearchParams();
	if (shouldCarryMockMode()) {
		params.set("mock", "1");
	}
	params.set("replay_context", "1");
	params.set("session_id", data.session_id);
	params.set("message", "");
	params.set("history", JSON.stringify(messages));
	return `/pipeline-testbench?${params.toString()}`;
}

function shouldCarryMockMode(): boolean {
	if (typeof window === "undefined") return false;
	return new URLSearchParams(window.location.search).get("mock") === "1";
}

function finalOutcomeTone(outcome: string): RunDetailTone {
	if (outcome === "failed" || outcome === "handoff") return "blocked";
	if (outcome === "partial") return "warning";
	if (outcome === "success") return "success";
	return "neutral";
}

function finalOutcomeLabel(outcome: string): string {
	if (outcome === "success") return "已完成";
	if (outcome === "partial") return "部分完成";
	if (outcome === "handoff") return "转人工预留";
	if (outcome === "failed") return "失败";
	return outcome;
}

function closeRecommendationTone(recommendation: TraceEvalSummary["close_recommendation"]) {
	if (recommendation === "ready_to_close") {
		return {
			label: "建议收口",
			className: "border-brand-success/30 bg-brand-success/10 text-brand-success",
		};
	}
	if (recommendation === "needs_follow_up") {
		return {
			label: "继续修复",
			className: "border-brand-danger/30 bg-brand-danger/10 text-brand-danger",
		};
	}
	return {
		label: "待判断",
		className: "border-surface-border bg-surface-muted text-text-secondary",
	};
}

function rerunStatusLabel(status: string | null): string {
	if (status === "rerun_passed") return "复跑通过";
	if (status === "rerun_failed") return "复跑仍失败";
	if (status === "not_rerun") return "待复跑";
	return "未返回";
}

function shortId(value: string): string {
	return value.length > 12 ? `${value.slice(0, 8)}...` : value;
}

function IntentSection({ event }: { event: IntentRecognitionEvent }) {
	return (
		<Card data-testid="trace-intent-section">
			<CardHeader>
				<CardTitle>意图识别</CardTitle>
				<CardDescription>T2 IntentDispatcher 三档级联输出</CardDescription>
			</CardHeader>
			<CardContent>
				<dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
					<Field label="意图" value={event.intent} testid="intent-field-intent" />
					<Field
						label="置信度"
						value={event.confidence.toFixed(2)}
						testid="intent-field-confidence"
					/>
					<Field label="实现层级" value={event.implementation_level} testid="intent-field-level" />
					<Field label="服务阶段" value={event.service_stage} testid="intent-field-stage" />
					<Field
						label="L2 命中规则"
						value={event.l2_matched_rule_id ?? "—"}
						testid="intent-field-rule"
					/>
					<Field label="主题" value={event.topic ?? "—"} testid="intent-field-topic" />
					<Field
						label="次意图"
						value={event.secondary_intent ?? "—"}
						testid="intent-field-secondary"
					/>
					<Field label="连续性" value={event.intent_continuity} testid="intent-field-continuity" />
				</dl>
			</CardContent>
		</Card>
	);
}

function PlaybookSection({ event }: { event: PlaybookRecommendationEvent }) {
	return (
		<Card data-testid="trace-playbook-section">
			<CardHeader>
				<CardTitle>处理路径推荐</CardTitle>
				<CardDescription>RoutingTable 优先；未命中时再走兜底映射、语义兜底或跳过。</CardDescription>
			</CardHeader>
			<CardContent>
				<dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
					<Field label="处理路径" value={traceRouteLabel(event)} testid="playbook-field-id" />
					<Field label="方案名称" value={event.playbook_name ?? "—"} testid="playbook-field-name" />
					<Field
						label="版本"
						value={event.playbook_version ?? "—"}
						testid="playbook-field-version"
					/>
					<Field label="匹配模式" value={event.match_mode} testid="playbook-field-mode" />
					<Field
						label="规则 ID"
						value={event.routing_rule_id ?? "—"}
						testid="playbook-field-rule"
					/>
					<SkillField
						label="推荐 Skills"
						items={event.recommended_skills}
						testid="playbook-field-skills"
					/>
					<SkillField
						label="通用 Skills"
						items={event.universal_skills}
						testid="playbook-field-universal"
					/>
				</dl>
			</CardContent>
		</Card>
	);
}

function Field({ label, value, testid }: { label: string; value: string; testid: string }) {
	return (
		<>
			<dt className="text-text-secondary">{label}</dt>
			<dd className="text-text-primary" data-testid={testid}>
				{value}
			</dd>
		</>
	);
}

function SkillField({
	label,
	items,
	testid,
}: {
	label: string;
	items: string[];
	testid: string;
}) {
	return (
		<>
			<dt className="text-text-secondary">{label}</dt>
			<dd className="text-text-primary" data-testid={testid}>
				{items.length === 0 ? "—" : items.join(" · ")}
			</dd>
		</>
	);
}
